import { ApolloClient, from, HttpLink } from "@apollo/client"
import { setContext } from "apollo-link-context"
import { InMemoryCache } from "@apollo/client/cache"

import WatchedMutationLink from "./apollo-link-watched-mutation-master"

const ENV = process.env.NODE_ENV
const DEV_URL = "http://localhost:4000/graphql"
const PROD_URL = "https://us-central1-grouptravelres.cloudfunctions.net/api"
const uri = ENV === "development" ? DEV_URL : PROD_URL
// const uri = PROD_URL

const cache = new InMemoryCache({ addTypename: false })

const authMiddleware = setContext((_, { headers }) => {
  const token = localStorage.getItem("token")
  return {
    headers: {
      ...headers,
      authorization: `Bearer ${token}`,
    },
  }
})

const watchMutation = new WatchedMutationLink(cache, {
  createBooking: {
    Bookings: ({ mutation, query }) => {
      const createdBooking = mutation.result.data.createBooking
      const cachedBookings = query.result.bookings
      return {
        bookings: [createdBooking, ...cachedBookings],
      }
    },
  },

  DeleteBooking: {
    Bookings: ({ mutation, query }) => {
      const deletedBookingId = mutation.variables.id
      const cachedBookings = query.result.trips
      return {
        bookings: cachedBookings.filter((_) => _.id !== deletedBookingId),
      }
    },
  },

  CancelBooking: {
    Bookings: ({ mutation, query }) => {
      const canceldBooking = mutation.result.data.cancelBooking
      const cachedBookings = query.result.bookings
      return {
        bookings: cachedBookings.map((booking) => ({
          ...booking,
          ...(booking.id === canceldBooking.id ? { ...canceldBooking } : {}),
        })),
      }
    },
  },

  CreateFiles: {
    Files: ({ mutation, query }) => {
      const createdFiles = mutation.result.data.createFiles
      const cachedFiles = query.result.files

      return {
        files: [...createdFiles, ...cachedFiles],
      }
    },
  },

  UpdateFile: {
    Files: ({ mutation, query }) => {
      const changedFile = mutation.result.data.updateFile
      const cachedFiles = query.result.files

      return {
        files: cachedFiles.map((_) => ({
          ..._,
          ...(changedFile.id === _.id ? { ...changedFile } : {}),
        })),
      }
    },
  },

  DeleteFile: {
    Files: ({ mutation, query }) => {
      const deletedFileId = mutation.variables.id
      const cachedFiles = query.result.files
      return {
        files: cachedFiles?.filter((_) => _.id !== deletedFileId),
      }
    },
  },

  CreateTrip: {
    Trips: ({ mutation, query }) => {
      const createdGroup = mutation.result.data.createTrip
      const cachedGroups = query.result.trips
      return {
        trips: [...cachedGroups, createdGroup],
      }
    },
  },

  UpdateTripStatus: {
    Trips: ({ mutation, query }) => {
      const changedGroup = mutation.result.data.updateTripStatus
      const cachedGroups = query.result.trips

      return {
        trips: cachedGroups.map((group) => ({
          ...group,
          status:
            group.id !== changedGroup.id ? changedGroup.status : group.status,
        })),
      }
    },
  },

  DeleteTrip: {
    Trips: ({ mutation, query }) => {
      const deletedGroupId = mutation.variables.id
      const cachedGroups = query.result.trips
      return {
        trips: cachedGroups.filter((_) => _.id !== deletedGroupId),
      }
    },
  },

  CreateAgency: {
    Agencies: ({ mutation, query }) => {
      const createdAgency = mutation.result.data.createAgency
      const cachedAgency = query.result.agencies

      return {
        agencies: [...cachedAgency, createdAgency],
      }
    },
  },

  UpdateAgency: {
    Agencies: ({ mutation, query }) => {
      const changedAgency = mutation.result.data.updateAgency
      const cachedAgency = query.result.agencies

      return {
        agencies: cachedAgency.map((passenger) => ({
          ...passenger,
          ...(changedAgency.id === passenger.id ? { ...changedAgency } : {}),
        })),
      }
    },
  },

  DeleteAgency: {
    Agencies: ({ mutation, query }) => {
      const deletedAgencyId = mutation.variables.id
      const cachedAgencies = query.result.agencies
      return {
        agencies: cachedAgencies?.filter((_) => _.id !== deletedAgencyId),
      }
    },
  },

  CreatePassenger: {
    Passengers: ({ mutation, query }) => {
      const createdPassenger = mutation.result.data.createPassenger
      const cachedPassenger = query.result.passengers

      return {
        passengers: [...cachedPassenger, createdPassenger],
      }
    },
  },

  UpdatePassenger: {
    Passengers: ({ mutation, query }) => {
      const changedPassenger = mutation.result.data.updatePassenger
      const cachedPassenger = query.result.passengers

      return {
        passengers: cachedPassenger.map((passenger) => ({
          ...passenger,
          ...(changedPassenger.id === passenger.id
            ? { ...changedPassenger }
            : {}),
        })),
      }
    },
  },

  DeletePassenger: {
    Passengers: ({ mutation, query }) => {
      const deletedPassengerId = mutation.variables.id
      const cachedPassengers = query.result.passengers
      return {
        passengers: cachedPassengers?.filter(
          (_) => _.id !== deletedPassengerId,
        ),
      }
    },
  },

  CreateUser: {
    Users: ({ mutation, query }) => {
      const createdUser = mutation.result.data.createUser
      const cachedUsers = query.result.users
      return {
        users: [...cachedUsers, createdUser],
      }
    },
  },

  UpdateUser: {
    Users: ({ mutation, query }) => {
      const updatedUser = mutation.result.data.updateUser
      const cachedUsers = query.result.users

      return {
        users: cachedUsers.map((user) => ({
          ...user,
          ...(user.id === updatedUser.id ? updatedUser : {}),
        })),
      }
    },
  },

  DeleteUser: {
    Users: ({ mutation, query }) => {
      const deletedUserId = mutation.variables.id
      const cachedUsers = query.result.users
      return {
        users: cachedUsers.filter((_) => _.id !== deletedUserId),
      }
    },
  },
})

const link = from([
  // new RetryLink(),
  // new MyAuthLink(),
  authMiddleware,
  watchMutation,
  new HttpLink({ uri }),
])

export default new ApolloClient({ link, cache })
